import React, { useEffect, useRef, useState } from "react";
import { useState as useStateModal } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { Modal } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useQueryClient } from "@tanstack/react-query";
import { Check, MoreVertical, Pencil, Trash, Video, XCircle } from "lucide-react";

import { DeleteModal } from "components/AppointmentsList/components/ConsultationItem/ConsultationItem/DeleteModal/DeleteModal";
import { CancelAppointmentModal } from "components/Modal/CancelAppointmentModal";
import { useCancelAppointment } from "features/appointments/hooks/useCancelAppointment";
import { useDeleteAppointment } from "features/appointments/hooks/useDeleteAppointment";
import { useUpdateAppointmentStatus } from "features/appointments/hooks/useUpdateAppointmentStatus";
import { useUpdateConsultationType } from "features/appointments/hooks/useUpdateConsultationType";
import { usePracticeServices } from "features/practice/hooks/usePracticeServices";
import { usePracticeSettings } from "features/practice/hooks/usePracticeSettings";
import { pathGenerators } from "utils/routes";
import { isNoShowHidden } from "utils/tenantOverrides";

import styles from "./BoardAppointmentItem.module.css";

const MINUTES_PER_HOUR = 60;

// Whole-day blocks run into the hundreds of minutes, which nobody reads as a
// duration — anything past the hour is shown as hours and minutes.
const formatDuration = (minutes) => {
  if (minutes < MINUTES_PER_HOUR) {
    return `${minutes}min`;
  }

  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  const remainingMinutes = minutes % MINUTES_PER_HOUR;

  return remainingMinutes ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
};

const getAppointmentStatusColor = (status) => {
  if (status === "NO_SHOW") return styles.noShow;
  if (status === "ARRIVED") return styles.arrived;
  if (status === "IN_CONSULTATION") return styles.inConsultation;
  if (status === "COMPLETED") return styles.completed;
  if (status === "UNAVAILABLE") return styles.unavailableAppointment;
  if (status === "DUE") return styles.due;

  // Anything else is a status this board has no word for, and it should not borrow Due's.
  return styles.default;
};

// `dotClass` names the same stylesheet block the card itself is painted with, so the
// swatch in the menu cannot drift away from the colour on the board — statusColours.test
// asserts the two stay equal.
const STATUS_OPTIONS = [
  { value: "ARRIVED", label: "Arrived", dotClass: "dotArrived", pillClass: "pillArrived" },
  {
    value: "IN_CONSULTATION",
    label: "In Consultation",
    dotClass: "dotInConsultation",
    pillClass: "pillInConsultation",
  },
  { value: "COMPLETED", label: "Completed", dotClass: "dotCompleted", pillClass: "pillCompleted" },
  { value: "DUE", label: "Due", dotClass: "dotDue", pillClass: "pillDue" },
  { value: "NO_SHOW", label: "DNA", dotClass: "dotNoShow", pillClass: "pillNoShow" },
];

const statusOption = (status) =>
  STATUS_OPTIONS.find((option) => option.value === status) ?? STATUS_OPTIONS[3];

const ChangeStatusButton = ({
  disabled,
  appointmentId,
  currentStatus,
  onSelect,
  deleteAppointment,
  editAppointment,
  setupVideo,
  isVideoAppointment,
  allowDelete,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { mutate: updateAppointmentStatus } =
    useUpdateAppointmentStatus(onSelect);

  const { mutate: cancelAppointment, isPending: isCancelling } =
    useCancelAppointment(onSelect);

  const handleCancelConfirm = (cancellationReason) => {
    cancelAppointment({
      id: appointmentId,
      cancellationReason,
    });
    setShowCancelModal(false);
  };

  return (
    <>
      <div className={styles.changeStatusButtonWrapper}>
        <button
          id="sortBy"
          aria-controls={open ? "sortBy" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          className={styles.sortByButton}
          disabled={disabled}
          aria-label="change status"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <MoreVertical size={16} width={16} height={16} />
        </button>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          sx={{
            "& .MuiPaper-root": {
              borderRadius: "12px",
              minWidth: "180px",
            },
          }}
          className={styles.menuItemWrapper}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <p className={styles.menuTitle} style={{ marginTop: "2px" }}>
            Actions
          </p>
          <MenuItem
            onClick={() => {
              handleClose();
              editAppointment();
            }}
            className={styles.menuItem}
          >
            <button className={styles.editButton}>
              <Pencil
                size={14}
                width={14}
                height={14}
                className={styles.editIcon}
              />
              Edit
            </button>
          </MenuItem>
          {!!allowDelete && (
            <MenuItem
              onClick={() => {
                handleClose();
                deleteAppointment(appointmentId);
              }}
              className={styles.menuItem}
            >
              <button className={styles.deleteButton}>
                <Trash
                  size={14}
                  width={14}
                  height={14}
                  className={styles.deleteIcon}
                />
                Delete
              </button>
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              handleClose();
              setShowCancelModal(true);
            }}
            className={styles.menuItem}
          >
            <button className={styles.cancelButton}>
              <XCircle
                size={14}
                width={14}
                height={14}
                className={styles.cancelIcon}
              />
              Cancel
            </button>
          </MenuItem>
          {!isVideoAppointment && (
            <MenuItem
              onClick={() => {
                handleClose();
                setupVideo?.();
              }}
              className={styles.menuItem}
            >
              <button className={styles.videoButton}>
                <Video
                  size={14}
                  width={14}
                  height={14}
                  className={styles.videoIcon}
                />
                Setup Video Call
              </button>
            </MenuItem>
          )}
          {/* The heading carries where the appointment stands now, so the menu answers
              "did that click land?" without closing it to read the card. */}
          <p
            className={`${styles.menuTitle} ${styles.menuTitleWithStatus}`}
            style={{ marginTop: "6px" }}
          >
            Change Status
            <span
              className={`${styles.menuCurrentStatus} ${
                styles[statusOption(currentStatus).pillClass]
              }`}
            >
              {statusOption(currentStatus).label}
            </span>
          </p>
          {STATUS_OPTIONS.filter(
            (option) => option.value !== "NO_SHOW" || !isNoShowHidden()
          ).map((option) => {
            const isCurrent = option.value === currentStatus;

            return (
              <MenuItem
                key={option.value}
                onClick={() => {
                  handleClose();
                  if (isCurrent) return;

                  updateAppointmentStatus({
                    id: appointmentId,
                    status: option.value,
                  });
                }}
                className={`${styles.menuItem} ${
                  isCurrent ? styles.menuItemCurrent : ""
                }`}
                aria-current={isCurrent ? "true" : undefined}
              >
                <span
                  className={`${styles.statusDot} ${styles[option.dotClass]}`}
                  aria-hidden
                />
                <button>{option.label}</button>
                {!!isCurrent && (
                  <Check size={14} className={styles.menuCheck} aria-hidden />
                )}
              </MenuItem>
            );
          })}
        </Menu>
      </div>

      <CancelAppointmentModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelConfirm}
        isLoading={isCancelling}
      />
    </>
  );
};

const appointmentServiceToDotColor = (id) => {
  if (id === 51) return "#FF6726";
  if (!id) return "#808080";

  const hash = Math.abs((id * 9301 + 49297) % 233280);

  const r = (hash % 255).toString(16).padStart(2, "0");
  const g = ((hash * 3) % 255).toString(16).padStart(2, "0");
  const b = ((hash * 7) % 255).toString(16).padStart(2, "0");

  return `#${r}${g}${b}`;
};

export const BoardAppointmentItem = ({
  item,
  doctor,
  onAppointmentClick,
  onConsultationEdit,
  isSelected,
  onCheckboxToggle,
  slotDurationMinutes,
  // Overbooked slot: the card shares a column with another and renders trimmed
  // (no duration badge / menu) until hovered, so both fit without being cut.
  compact = false,
}) => {
  const elementRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDropOver, setIsDropOver] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useStateModal(false);

  const queryClient = useQueryClient();
  const { data: services } = usePracticeServices();
  const { data: practiceSettings } = usePracticeSettings();
  const consultation = item?.meeting?.consultation;
  const navigate = useNavigate();

  const { mutate: deleteAppointment } = useDeleteAppointment(() => {
    queryClient.invalidateQueries({ queryKey: ["appointments"] });
    setShowDeleteModal(false);
  });

  const { mutate: updateConsultationType } = useUpdateConsultationType(() => {
    queryClient.invalidateQueries({ queryKey: ["appointments"] });
  });

  const handleSetupVideo = () => {
    if (consultation?.id) {
      updateConsultationType({
        appointmentId: consultation.id,
        consultationType: "VIDEO",
      });
    }
  };

  useEffect(() => {
    if (!consultation) return;

    const element = elementRef.current;
    if (!element) return;

    let cleanup;
    try {
      cleanup = draggable({
        element,
        getInitialData: () => ({ item, doctor, type: "appointment" }),
        onDragStart: () => {
          setIsDragging(true);
        },
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          try {
            const dragPreview = document.createElement("div");
            dragPreview.innerHTML = `
              <div style="
                background: #006546;
                color: white;
                padding: 12px 20px;
                border-radius: 12px;
                font-size: 16px;
                font-weight: bold;
                text-align: center;
                box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
                border: 2px solid #fff;
                min-width: 200px;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
              ">
                <span style="font-size: 18px;">📅</span>
                <span>Moving Appointment</span>
                ${
                  consultation?.patient
                    ? `<br><small style="font-size: 12px; opacity: 0.9;">${consultation.patient.firstName} ${consultation.patient.surname}</small>`
                    : ""
                }
              </div>
            `;

            nativeSetDragImage(dragPreview.firstElementChild, 100, 30);
          } catch (error) {
            console.warn("Failed to create drag preview:", error);
          }
        },
        onDrop: () => {
          setIsDragging(false);
        },
      });
    } catch (error) {
      console.warn("Failed to setup draggable:", error);
    }

    // Also a drop target: dropping another appointment onto this card books it
    // into THIS card's slot (overbooking). The reschedule flow reuses the Add
    // form, so the overlap gate (allowOverlappingAppointments + max 2 +
    // warn/confirm) is enforced there. Dropping a card on itself is ignored by
    // the board monitor (source.element === destination.element).
    let dropCleanup;
    try {
      dropCleanup = dropTargetForElements({
        element,
        getData: () => ({ item, doctor, type: "occupied-slot" }),
        canDrop: ({ source }) => source.data.type === "appointment",
        onDragEnter: () => setIsDropOver(true),
        onDragLeave: () => setIsDropOver(false),
        onDrop: () => setIsDropOver(false),
      });
    } catch (error) {
      console.warn("Failed to setup drop target:", error);
    }

    return () => {
      if (typeof cleanup === "function") {
        try {
          cleanup();
        } catch (error) {
          console.warn("Failed to cleanup draggable:", error);
        }
      }
      if (typeof dropCleanup === "function") {
        try {
          dropCleanup();
        } catch (error) {
          console.warn("Failed to cleanup drop target:", error);
        }
      }
    };
  }, [consultation, item, doctor]);

  if (!consultation) {
    return null;
  }

  const clickOnAppointment = (e) => {
    // Allow default for middle-click or ctrl+click (open in new tab)
    if (e.button === 1 || e.ctrlKey || e.metaKey) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (consultation?.patient?.id) {
      navigate(pathGenerators.patient.consult(consultation.patient.id));
    }
  };

  const isUnavailableStatus = consultation?.appointmentStatus === "UNAVAILABLE";

  const patientName = consultation?.patient
    ? `${consultation.patient.firstName} ${consultation.patient.surname}`
    : isUnavailableStatus
    ? "Unavailable"
    : "Unknown Patient";


  const startTime = new Date(consultation.startTime);
  const endTime = new Date(consultation.endTime);
  const durationMinutes = Math.round((endTime - startTime) / (1000 * 60));

  // Due is what almost every card is, so it gets no badge — a label on six hundred cards
  // saying nothing has happened is noise, and it would crowd out the ones that mean
  // something. Short cards have no room for it either.
  const showStatusPill =
    !isUnavailableStatus &&
    !compact &&
    durationMinutes >= 15 &&
    !!consultation?.appointmentStatus &&
    consultation.appointmentStatus !== "DUE";

  // The smallest an appointment can render is one whole slot, so a 10-min
  // appointment in 15-min slots still fills a full 15-min row.
  const slotHeightPx = slotDurationMinutes === 5 ? 45 : 60;
  // Exact height of the slot row(s) this appointment occupies (rows are
  // 60/45px with a 12px grid gap between them).
  const cardHeightPx =
    (item.rowSpan || 1) * slotHeightPx +
    Math.max(0, (item.rowSpan || 1) - 1) * 12;

  const getDurationClass = (duration) => {
    if (duration <= 5 && slotDurationMinutes === 5) return "duration-5-5";
    if (duration <= 5 && slotDurationMinutes === 15) return "duration-5-15";
    if (duration <= 10 && slotDurationMinutes === 5) return "duration-10-5";
    if (duration <= 10 && slotDurationMinutes === 15) return "duration-10-15";
    if (duration <= 15 && slotDurationMinutes === 5) return "duration-15-5";
    if (duration <= 15 && slotDurationMinutes === 15) return "duration-15-15";
    if (duration <= 20 && slotDurationMinutes === 5) return "duration-20-5";
    if (duration <= 20 && slotDurationMinutes === 15) return "duration-20-15";
    if (duration <= 30 && slotDurationMinutes === 5) return "duration-30-5";
    if (duration <= 30 && slotDurationMinutes === 45) return "duration-30-15";
    if (duration <= 45 && slotDurationMinutes === 5) return "duration-45-5";
    if (duration <= 45 && slotDurationMinutes === 15) return "duration-45-15";
    if (duration <= 60 && slotDurationMinutes === 5) return "duration-60-5";
    if (duration <= 60 && slotDurationMinutes === 15) return "duration-60-15";
    return "";
  };

  const isVaccination =
    services
      ?.find((service) => service.id === consultation?.practiceServiceId)
      ?.serviceName?.toLowerCase()
      .includes("vaccine") ||
    services
      ?.find((service) => service.id === consultation?.practiceServiceId)
      ?.serviceName?.toLowerCase()
      .includes("vaccination");

  const service = services?.find(
    (service) => service.id === consultation?.practiceServiceId
  );
  const serviceColor =
    service?.color ||
    appointmentServiceToDotColor(consultation?.practiceServiceId);

  return (
    <>
      <div
        ref={elementRef}
        className={`${styles.boardAppointmentItem} ${getAppointmentStatusColor(
          consultation.appointmentStatus
        )} ${styles[getDurationClass(durationMinutes)]} ${
          isVaccination ? styles.vaccination : ""
        } ${isDragging ? styles.isDragging : ""} ${
          compact ? styles.compact : ""
        }`}
        style={{
          // Single source of truth for card height: the exact size of the slot
          // row(s) it spans. height + maxHeight (inline) override the assorted
          // .duration-* CSS heights/max-heights so a multi-slot appointment
          // (e.g. 15 min in 5-min slots = 3 slots) is never capped, and a
          // sub-slot one (10 min in 15-min slots) never bleeds past its slot.
          // overflow:hidden on the card clips any content that doesn't fit.
          height: `${cardHeightPx}px`,
          maxHeight: `${cardHeightPx}px`,
          // Transparent border keeps the 8px of box model the strip paints over
          // (see .boardAppointmentItem::after), so paddings stay untouched.
          borderLeft: "8px solid transparent",
          "--service-color": serviceColor,
          // Highlight when another appointment is dragged over this card (drop
          // here = overbook into this slot).
          outline: isDropOver ? "2px dashed #006546" : undefined,
          outlineOffset: isDropOver ? "-2px" : undefined,
        }}
        onClick={() => onAppointmentClick?.(item)}
      >
        <div className={styles.appointmentHeader}>
          <div className={styles.appointmentHeaderLeft}>
            {!isUnavailableStatus && (
              <input
                type="checkbox"
                checked={isSelected || false}
                onChange={(e) => {
                  e.stopPropagation();
                  onCheckboxToggle?.(consultation.id);
                }}
                onClick={(e) => e.stopPropagation()}
                className={styles.appointmentCheckbox}
                aria-label={`Select appointment for ${patientName}`}
              />
            )}
            {consultation?.patient?.id ? (
              <Link
                to={pathGenerators.patient.consult(consultation.patient.id)}
                className={styles.patientName}
                onClick={clickOnAppointment}
                data-testid="patient-name"
                title={patientName}
              >
                {patientName}
              </Link>
            ) : (
              <span
                className={styles.patientName}
                data-testid="patient-name"
                title={patientName}
              >
                {patientName}
              </span>
            )}
          </div>
          <div className={styles.durationBadge}>
            {formatDuration(durationMinutes)}
          </div>
          {!isUnavailableStatus && (
            <div className={styles.appointmentActions}>
            <ChangeStatusButton
              appointmentId={consultation?.id}
              currentStatus={consultation?.appointmentStatus}
              onSelect={() =>
                queryClient.invalidateQueries({ queryKey: ["appointments"] })
              }
              editAppointment={onConsultationEdit}
              deleteAppointment={() => setShowDeleteModal(true)}
              setupVideo={handleSetupVideo}
              isVideoAppointment={consultation?.consultationType === "VIDEO"}
              allowDelete={!!practiceSettings?.allowDeleteAppointments}
            />
            </div>
          )}
        </div>

        <div className={styles.serviceRow}>
        <div className={styles.serviceInfo}>
          <span
            className={styles.serviceDot}
            style={{
              backgroundColor: isUnavailableStatus
                ? "#60a5fa"
                : appointmentServiceToDotColor(consultation?.practiceServiceId),
            }}
          />
          <span className={styles.serviceName}>
            {isUnavailableStatus
              ? "Outside practice hours"
              : consultation?.practiceService?.name || "Unknown Service"}
          </span>
          {consultation?.consultationType === "VIDEO" && (
            <Video
              size={14}
              className={styles.videoIndicator}
              color="#006546"
              title="Video Appointment"
            />
          )}
        </div>
          {/* Names the status outright, so the card does not rely on its colour alone —
              which is what failed reception when two fills drifted together. Beside the
              service box rather than inside it, and never for Due: that is what almost
              every card is, and labelling it would put a badge on six hundred cards to
              say nothing has happened. Dropped on short cards, which have no room. */}
          {!!showStatusPill && (
            <span
              className={`${styles.statusPill} ${
                styles[statusOption(consultation?.appointmentStatus).pillClass]
              }`}
            >
              {statusOption(consultation?.appointmentStatus).label}
            </span>
          )}
        </div>

      </div>

      {showDeleteModal && (
        <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
          <DeleteModal
            open={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            description="Are you sure you want to delete this appointment?"
            onSave={() => {
              if (item?.meeting?.consultation?.id) {
                deleteAppointment(item.meeting.consultation.id);
              }
            }}
            width="500px"
            saveBtnTitle="Yes"
          >
            <div className="modal-body">
              <div className={styles.warningImg}>
                <span>⚠️</span>
              </div>
              <div className={styles.deleteText}>
                <h5>Are you sure you want to delete this appointment?</h5>
              </div>
            </div>
          </DeleteModal>
        </Modal>
      )}
    </>
  );
};
